import {
  CardAvatar,
  CardListEmpty,
  CardRow,
  CardRowDeleteButton,
} from '@/components/data-table/card-list';
import {
  ClearFiltersButton,
  MultiSelectFilter,
  SearchInput,
} from '@/components/data-table/filters';
import { PaginationFooter } from '@/components/data-table/PaginationFooter';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import type {
  OrganizationTravelEsim,
  PaginationInfo,
  TravelEsimBeneficiaryKind,
  TravelEsimQrCodeState,
  TravelEsimStayState,
} from '@octave/contracts';
import { Globe, Mail, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const QR_CODE_STATE_OPTIONS: { value: TravelEsimQrCodeState; label: string }[] = [
  { value: 'pending', label: 'En attente' },
  { value: 'sent', label: 'Envoyé' },
];

const BENEFICIARY_OPTIONS: { value: TravelEsimBeneficiaryKind; label: string }[] =
  [
    { value: 'fleet', label: 'Membre de la flotte' },
    { value: 'external', label: 'Hors flotte' },
  ];

const STAY_OPTIONS: { value: TravelEsimStayState; label: string }[] = [
  { value: 'upcoming', label: 'À venir' },
  { value: 'ongoing', label: 'En cours' },
  { value: 'past', label: 'Terminé' },
];


export interface TravelEsimFilters {
  qrCode?: TravelEsimQrCodeState[];
  beneficiary?: TravelEsimBeneficiaryKind[];
  stay?: TravelEsimStayState[];
}

interface DataTableProps {
  data: OrganizationTravelEsim[];
  pagination: { page: number; limit: number };
  pageInfo: PaginationInfo;
  onPaginationChange: (pagination: { page: number; limit: number }) => void;
  onFiltersChange: (filters: TravelEsimFilters) => void;
  onSearchChange: (searchText: string) => void;
  currentFilters: TravelEsimFilters;
  currentSearchText: string;
  isFetching?: boolean;
  /** Octave admins only: deletion action on each row. */
  onDelete?: (esim: OrganizationTravelEsim) => void;
}

function initialsOf(name: string): string | null {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

function plansLabel(count: number): string {
  return count > 1 ? `${count} forfaits` : `${count} forfait`;
}

/** Card list of the organization's international eSIMs, one row per eSIM. */
export function DataTable({
  data,
  pagination,
  pageInfo,
  onPaginationChange,
  onFiltersChange,
  onSearchChange,
  currentFilters,
  currentSearchText,
  isFetching,
  onDelete,
}: DataTableProps) {
  const navigate = useNavigate();
  const { tenantSlug } = useTenantSlug();

  const toggle = <K extends keyof TravelEsimFilters>(
    key: K,
    value: NonNullable<TravelEsimFilters[K]>[number],
    checked: boolean,
  ) => {
    const current = (currentFilters[key] ?? []) as string[];
    const next = checked
      ? [...current, value]
      : current.filter((v) => v !== value);
    onFiltersChange({
      ...currentFilters,
      [key]: next.length > 0 ? next : undefined,
    });
  };

  const hasActiveFilters =
    !!currentSearchText ||
    Object.values(currentFilters).some((values) => (values?.length ?? 0) > 0);

  return (
    <div className="space-y-5">
      {/* Filters bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <SearchInput
          placeholder="Rechercher un bénéficiaire, une destination..."
          value={currentSearchText}
          onChange={onSearchChange}
        />
        <MultiSelectFilter
          label="Séjour"
          options={STAY_OPTIONS}
          selected={currentFilters.stay ?? []}
          onToggle={(value, checked) =>
            toggle('stay', value as TravelEsimStayState, checked)
          }
        />
        <MultiSelectFilter
          label="Bénéficiaire"
          options={BENEFICIARY_OPTIONS}
          selected={currentFilters.beneficiary ?? []}
          onToggle={(value, checked) =>
            toggle('beneficiary', value as TravelEsimBeneficiaryKind, checked)
          }
        />
        <MultiSelectFilter
          label="QR code"
          options={QR_CODE_STATE_OPTIONS}
          selected={currentFilters.qrCode ?? []}
          onToggle={(value, checked) =>
            toggle('qrCode', value as TravelEsimQrCodeState, checked)
          }
        />
        {hasActiveFilters && (
          <ClearFiltersButton
            onClick={() => {
              onSearchChange('');
              onFiltersChange({});
            }}
          />
        )}
      </div>

      {/* Cards list */}
      <div className="relative">
        {isFetching && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex items-center justify-center rounded-2xl">
            <div className="flex items-center gap-2.5 text-sm text-slate-500 bg-white px-4 py-2 rounded-full shadow-sm">
              <div className="animate-spin h-4 w-4 border-2 border-emerald-500 border-t-transparent rounded-full" />
              Mise à jour...
            </div>
          </div>
        )}

        <div className="space-y-2">
          {data.length ? (
            data.map((esim) => {
              const { beneficiary } = esim;
              return (
                <CardRow
                  key={esim.id}
                  onClick={() => navigate(`/o/${tenantSlug}/travel-esims/${esim.id}`)}
                >
                  <CardAvatar
                    initials={initialsOf(beneficiary.name)}
                    fallbackIcon={Globe}
                  />

                  <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-4 gap-x-4 items-center">
                    {/* Col 1: beneficiary */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">
                          {beneficiary.name}
                        </p>
                        <span
                          className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            beneficiary.kind === 'LINE'
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {beneficiary.kind === 'LINE' ? 'Flotte' : 'Hors flotte'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 truncate flex items-center gap-1 mt-0.5">
                        {beneficiary.kind === 'LINE' ? (
                          <>
                            <Phone className="h-3 w-3 shrink-0" />
                            <span className="tabular-nums tracking-wide">
                              {beneficiary.lineNumber}
                            </span>
                          </>
                        ) : (
                          <>
                            <Mail className="h-3 w-3 shrink-0" />
                            <span className="truncate">{beneficiary.email}</span>
                          </>
                        )}
                      </p>
                    </div>

                    {/* Col 2: eSIM number */}
                    <div className="min-w-0 hidden sm:block">
                      <p className="text-sm text-slate-700 truncate font-mono">
                        {esim.esimNumber ?? 'Numéro à venir'}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">Numéro eSIM</p>
                    </div>

                    {/* Col 3: billing center */}
                    <div className="min-w-0 hidden sm:block text-center">
                      <p className="text-xs text-slate-500 truncate">
                        {esim.siteName ?? ''}
                      </p>
                    </div>

                    {/* Col 4: plans */}
                    <div className="min-w-0 hidden sm:flex justify-end">
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 whitespace-nowrap">
                        {plansLabel(esim.plansCount)}
                      </span>
                    </div>
                  </div>

                  {onDelete && (
                    <CardRowDeleteButton
                      label={`Supprimer l'eSIM de ${beneficiary.name}`}
                      onClick={() => onDelete(esim)}
                    />
                  )}
                </CardRow>
              );
            })
          ) : (
            <CardListEmpty
              icon={Globe}
              title="Aucune eSIM internationale"
              hint={
                hasActiveFilters
                  ? 'Essayez de modifier vos filtres'
                  : 'Commandez-en une depuis le catalogue'
              }
            />
          )}
        </div>
      </div>

      <PaginationFooter
        page={pagination.page}
        pageCount={pageInfo.nbPage || 1}
        summary={`${pageInfo.count} eSIM`}
        pageSize={pagination.limit}
        onPageSizeChange={(size) => onPaginationChange({ page: 1, limit: size })}
        onPageChange={(page) => onPaginationChange({ ...pagination, page })}
        isFetching={isFetching}
      />
    </div>
  );
}
